/* eslint-disable @typescript-eslint/no-explicit-any */
import toast from "react-hot-toast";
import {
  AuthData,
  LoginResponse,
  OTPResponse,
  SignupData,
  SignupResponse,
  UpdateUser,
  UserResponse,
} from "../types/appointmentType";
import { localStorageServices } from "../utils/localStorage";
import { clearSession } from "../utils/session";
import { isFramed } from "../utils/embed";

import { authApi } from "./axiosConfig";
import useAuth from "../store/useAuth";
import { useNavigate } from "react-router-dom";
import useAppointmentStore from "../store/useAppointmentStore";
import { apiCall } from "../helper/apiHelper";
export const signup = async (
  data: SignupData
): Promise<SignupResponse | null> => {
  try {
    
    const dummyEmail = `${data.firstName.toLowerCase()}.${data.lastName.toLowerCase()}.${data.phoneNumber}@primechart.com`;
    const userName = data.phoneNumber.replace(/\D/g, "").slice(-10); 

    const { firstName, middleName, lastName, password, phoneNumber, facilityId, birthDate, sex } = data;

    const dataToSend = {
      firstName,
      middleName,
      lastName,
      password,
      phoneNumber,
      facilityId,
      userCategoryId: 4,
      email: dummyEmail,
      userName: userName,
      birthDate: birthDate,
      sex: sex
    };


 
    const response = await apiCall({
      instance: authApi,
      url: "/User/CreateUser",
      method: "POST",
      data: dataToSend,
    });

    return response as SignupResponse;
  } catch (error) {
    const errorMessage =
      (error as Error).message || "An unknown error occurred during signup , please try again. ";
    toast.error(errorMessage);
 
    throw error;
  }
};

export const login = async (data: AuthData): Promise<LoginResponse | null> => {
  try {
    const response: any = await apiCall({
      instance: authApi,
      url: "/Auth/LoginByPhoneNumber",
      method: "POST",
      data,
    });

    if (!response?.successful && response?.errors) {
      const errorMessage = response.errors[0]?.messages?.[0] || "Invalid Phone Number or Password";
      const isAccountLockedError = errorMessage.toLowerCase().includes("account is locked");
      const error = new Error(errorMessage) as Error & { isAccountLocked?: boolean; errors?: any };
      error.isAccountLocked = isAccountLockedError;
      error.errors = response.errors;
      throw error;
    }

    const resData = response.item as LoginResponse;

    if (resData?.idToken) {
      toast.success("Login Successful");
      return resData;
    } else {
      const errorMessage = response?.errors?.[0]?.messages?.[0] || "Invalid Phone Number or Password";
      const error = new Error(errorMessage);
      (error as any).isAccountLocked = errorMessage.toLowerCase().includes("account is locked");
      throw error;
    }
  } catch (error: any) {
    if (error?.response?.data?.errors) {
      const errorMessage = error.response.data.errors[0]?.messages?.[0] || "Invalid Phone Number or Password";
      const loginError = new Error(errorMessage);
      (loginError as any).isAccountLocked = errorMessage.toLowerCase().includes("account is locked");
      (loginError as any).errors = error.response.data.errors;
      throw loginError;
    }
    throw error;
  }
};

export const updateProfile = async (
  data: UpdateUser
): Promise<UserResponse | null> => {
  const response = await apiCall({
    instance: authApi,
    url: "/User/UpdateUser",
    method: "POST",
    data,
  });

  return response as UserResponse;
};

export const changePassword = async (data: {
  userId: string;
  oldPassword: string;
  newPassword: string;
}): Promise<UserResponse | null> => {
  const response = await apiCall({
    instance: authApi,
    url: "/Auth/ChangePassword",
    method: "POST",
    data,
  });

  return response as UserResponse;
};

export const resetPassword = async (data: {
  userId: number;
  newPassword: string;
}): Promise<UserResponse | null> => {
  const response = await apiCall({
    instance: authApi,
    url: "/Auth/ResetPassword",
    method: "POST",
    data,
  });

  return response as UserResponse;
};

export const GetUserByPhoneNumber = async (
  phoneNumber: string
): Promise<UserResponse | null> => {
  return apiCall({
    instance: authApi,
    url: `/User/GetUserByPhoneNumber`,
    method: "GET",
    config: {
      params: { phoneNumber },
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const { clearUser } = useAuth();
  const clearAppointmentData = useAppointmentStore(state => state.clearAppointmentData);
  
  const logout = async (): Promise<void> => {
    try {
      sessionStorage.setItem("isLoggingOut", "true");
      
      clearUser();
      clearAppointmentData();
      localStorageServices.clearAuthData();
      // Thorough wipe on logout. Only inside an embed do we keep the facility scope
      // (so re-login stays on that hospital and the "needs a hospital link" guard
      // doesn't trip); a normal logout clears everything.
      clearSession({ keepScope: isFramed() });

      navigate("/auth?mode=signin", { replace: true });
      toast.success("Logged out successfully");
      
      setTimeout(() => {
        sessionStorage.removeItem("isLoggingOut");
      }, 500);
    } catch (err) {
      localStorageServices.clearAuthData();
      sessionStorage.removeItem("isLoggingOut");
      
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error("something went wrong");
      }
    }
  };

  return { logout, clearUser };
};
export const sendOtp = async (
  phoneNumber: string
): Promise<OTPResponse | null> => {
  try {
    const response: any = await apiCall({
      instance: authApi,
      url: `/User/SendOTP`,
      method: "POST",
      data: { phoneNumber },
    });

    if (response?.item?.acknowledge && response.item.acknowledge.includes("Too many OTP requests")) {
      const error = new Error(response.item.acknowledge);
      (error as any).isRateLimitError = true;
      throw error;
    }

    const otpResponse: OTPResponse = {
      item: response.item || { verificationId: "" },
      successful: response.successful || false,
    };

    return otpResponse;
  } catch (error: any) {
    const rateLimitMessage = error?.response?.data?.item?.acknowledge || 
                            error?.item?.acknowledge || 
                            (error?.message?.includes("Too many OTP requests") ? error.message : null);
    
    if (rateLimitMessage) {
      const rateLimitError = new Error(rateLimitMessage);
      (rateLimitError as any).isRateLimitError = true;
      throw rateLimitError;
    }
    throw error;
  }
};
export const verifyOtp = async (
  phoneNumber: string,
  otpCode: string,
  verificationId: string
): Promise<UserResponse | null> => {
  const response = await apiCall({
    instance: authApi,
    url: `/User/VerifyOTP`,
    method: "POST",
    data: { phoneNumber, otpCode, verificationId },
  });

  return response as UserResponse;
}
